import type { TFunction } from "i18next";
import type { ChangeEvent } from "react";

import { Paper, Stack } from "@mui/material";
import { AutocompleteField } from "components/AutocompleteField";
import { TrashIcon } from "components/IconsTextsUtils";
import { Input } from "components/Input";
import { useTranslation } from "react-i18next";

import type { LayerFormValue } from "../model/types";
import type { LayeredConstructionVariant } from "../model/variants";

type LayerCardProps = {
  index: number;
  layer: LayerFormValue;
  materialNames: string[];
  onChange: (index: number, field: keyof LayerFormValue, value: string) => void;
  onRemove: (index: number) => void;
  onSelectMaterial: (index: number, materialName: string) => void;
  variant: LayeredConstructionVariant;
};

const fieldLabel = (t: TFunction, variant: LayeredConstructionVariant, key: string) =>
  String(t(`TechnicalData.${variant.i18nKey}.layerFields.${key}`));

export const LayerCard = ({
  index,
  layer,
  materialNames,
  onChange,
  onRemove,
  onSelectMaterial,
  variant,
}: LayerCardProps) => {
  const { t } = useTranslation();

  const handleChange = (field: keyof LayerFormValue) => (event: ChangeEvent<HTMLInputElement>) =>
    onChange(index, field, event.target.value);

  const numericFields: [keyof LayerFormValue, keyof LayerFormValue][] = [
    ["thickness", "lambda"],
    ["density", "specificHeat"],
  ];

  return (
    <Paper className="w-full p-4">
      <Stack className="w-full flex-col items-stretch gap-3">
        <Stack className="w-full flex-row items-center justify-between">
          <span className="text-lds-foreground-strong font-ds-body-semibold-sm">
            {`${t(`TechnicalData.${variant.i18nKey}.layerLabel`)} ${index + 1}`}
          </span>
          <TrashIcon buttonProps={{ onClick: () => onRemove(index) }} />
        </Stack>
        <AutocompleteField<string>
          freeSolo
          getOptionLabel={(option) => String(option ?? "")}
          height="4.8rem"
          inputValue={layer.material}
          onChange={(_, value) => onSelectMaterial(index, typeof value === "string" ? value : String(value ?? ""))}
          onInputChange={(_, value, reason) => reason === "input" && onChange(index, "material", value)}
          options={materialNames}
          placeholder={fieldLabel(t, variant, "material")}
          value={layer.material}
        />
        {numericFields.map(([field1, field2]) => (
          <Stack className="w-full flex-row items-start gap-3" key={field1 + field2}>
            {[field1, field2].map((field) => (
              <Input
                className="flex-1"
                height="4.8rem"
                key={field}
                label={fieldLabel(t, variant, field)}
                onChange={handleChange(field)}
                type="number"
                value={layer[field]}
                width="100%"
              />
            ))}
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
};
